<script setup lang="ts">
import { useElementFullscreen } from "~/composables/useElementFullscreen";

const props = withDefaults(
  defineProps<{
    stream: MediaStream | null;
    presenterName: string;
    /** True when the local participant is the one presenting. */
    local?: boolean;
    /** True while local capture waits for the authoritative presenter ACK. */
    pending?: boolean;
  }>(),
  {
    local: false,
    pending: false,
  },
);

const emit = defineEmits<{
  "fullscreen-change": [fullscreen: boolean];
}>();

const stageElement = ref<HTMLElement | null>(null);
const videoElement = ref<HTMLVideoElement | null>(null);
const {
  isFullscreen,
  isFullscreenSupported,
  toggleFullscreen,
  exitFullscreen,
} = useElementFullscreen(stageElement);

const label = computed(() =>
  props.pending
    ? "Iniciando compartilhamento"
    : props.local
      ? "Você está compartilhando a tela"
      : `${props.presenterName} está compartilhando a tela`,
);

const attachStream = () => {
  if (videoElement.value && videoElement.value.srcObject !== props.stream) {
    videoElement.value.srcObject = props.stream;
  }
};

watch(
  () => props.stream,
  (stream) => {
    attachStream();
    if (!stream) exitFullscreen().catch(() => undefined);
  },
);
watch(isFullscreen, (fullscreen) => emit("fullscreen-change", fullscreen));
onMounted(attachStream);
onBeforeUnmount(() => {
  emit("fullscreen-change", false);
  if (videoElement.value) videoElement.value.srcObject = null;
});
</script>

<template>
  <section
    ref="stageElement"
    class="presentation-stage"
    :class="{ 'presentation-stage--fullscreen': isFullscreen }"
    :aria-label="label"
    data-testid="video-call-presentation-stage"
  >
    <video
      ref="videoElement"
      class="presentation-stage__video"
      autoplay
      playsinline
      muted
    />

    <button
      v-if="!props.local && isFullscreenSupported"
      type="button"
      class="presentation-stage__fullscreen"
      :aria-label="
        isFullscreen
          ? 'Sair da tela cheia'
          : 'Ver compartilhamento em tela cheia'
      "
      :title="
        isFullscreen
          ? 'Sair da tela cheia'
          : 'Ver compartilhamento em tela cheia'
      "
      data-testid="video-call-presentation-fullscreen-control"
      @click.stop="toggleFullscreen"
    >
      <i
        class="mdi"
        :class="isFullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen'"
        aria-hidden="true"
      />
    </button>

    <p
      v-if="props.pending || !props.stream"
      class="presentation-stage__placeholder"
      role="status"
    >
      <i class="mdi mdi-monitor-share" aria-hidden="true" />
      {{
        props.pending
          ? "Iniciando compartilhamento..."
          : "Carregando a tela compartilhada..."
      }}
    </p>

    <p class="presentation-stage__label">
      <i class="mdi mdi-monitor-share" aria-hidden="true" />
      {{ label }}
    </p>
  </section>
</template>

<style scoped lang="scss">
.presentation-stage {
  position: relative;
  display: flex;
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  align-items: center;
  justify-content: center;
  margin: 0;
  overflow: hidden;
  border: 1px solid #dbe1ef;
  border-radius: 14px;
  background: #0d1526;

  &:fullscreen,
  &.presentation-stage--fullscreen {
    width: 100%;
    height: 100%;
    max-height: none;
    margin: 0;
    border: 0;
    border-radius: 0;
  }

  &::backdrop {
    background: #0d1526;
  }
}

.presentation-stage__video {
  display: block;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  /* Never crop a shared screen: cropped slides lose the content that matters. */
  object-fit: contain;
  background: #0d1526;
}

.presentation-stage__fullscreen {
  position: absolute;
  z-index: 2;
  top: 10px;
  right: 10px;
  display: grid;
  width: 44px;
  height: 44px;
  padding: 0;
  place-items: center;
  border: 1px solid rgba(255, 255, 255, 0.24);
  border-radius: 12px;
  background: rgba(13, 21, 38, 0.82);
  color: #fff;
  cursor: pointer;
  font-size: 1.25rem;

  &:focus-visible {
    outline: 3px solid rgba(174, 196, 255, 0.92);
    outline-offset: 2px;
  }
}

.presentation-stage__placeholder {
  position: absolute;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  color: #b9c4dc;
  font-size: 0.85rem;
}

.presentation-stage__label {
  position: absolute;
  bottom: 10px;
  left: 10px;
  display: flex;
  align-items: center;
  gap: 6px;
  max-width: calc(100% - 20px);
  margin: 0;
  padding: 5px 10px;
  overflow: hidden;
  border-radius: 999px;
  background: rgba(13, 21, 38, 0.78);
  color: #fff;
  font-size: 0.75rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
